<template>
  <div class="login-container">
    <h2>Login to Mudra</h2>

    <form @submit.prevent="handleLogin">
      <!-- EMAIL -->
      <input
        type="email"
        v-model="email"
        placeholder="Email"
        aria-label="Email Address"
        required
      />

      <!-- PASSWORD -->
      <div class="password-wrapper">
        <input
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          placeholder="Password"
          aria-label="Password"
          required
        />
        <span class="eye-icon" @click="togglePassword">
          {{ showPassword ? '🔓' : '🔒' }}
        </span>
      </div>

      <p class="forgot-password" @click="resetPassword">
         🔑 Forgot your password?
      </p>

      <!-- STATUS MESSAGES -->
      <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>
      <p v-if="successMessage" class="success-msg">{{ successMessage }}</p>

      <!-- LOGIN BUTTON -->
      <button type="submit" :disabled="loading">
        <span v-if="loading" class="spinner"></span>
        {{ loading ? "Logging in..." : "Login" }}
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { supabase } from "@/supabase";

const email = ref("");
const password = ref("");
const showPassword = ref(false);
const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const router = useRouter();

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

// ✅ LOGIN FUNCTION
const handleLogin = async () => {
  errorMessage.value = "";
  successMessage.value = "";

  if (!email.value || !password.value) {
    errorMessage.value = "⚠️ Please enter both email and password.";
    return;
  }

  loading.value = true;

  const { error } = await supabase.auth.signInWithPassword({
    email: email.value.trim(),
    password: password.value,
  });

  loading.value = false;

  if (error) {
    errorMessage.value = "❌ " + error.message;
  } else {
    successMessage.value = "✅ Login successful! Redirecting...";
    setTimeout(() => router.push("/"), 1200);
  }
};

// ✅ RESET PASSWORD FUNCTION (Fixed safely)
const resetPassword = async () => {
  if (!email.value) {
    errorMessage.value = "Please enter your email to reset password.";
    return;
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email.value, {
    redirectTo: "https://mudra-token.web.app/reset-password",
  });

  if (error) {
    errorMessage.value = "❌ " + error.message;
  } else {
    successMessage.value = "✅ Password reset link sent! Check your inbox.";
  }
};
</script>

<style scoped>
/* ------------------------- */
/* LOGIN CONTAINER */
/* ------------------------- */
.login-container {
  max-width: 400px;
  margin: 10vh auto;
  padding: 40px 32px;
  background: rgba(13, 27, 42, 0.85);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e8f1f8;
  text-align: center;
  transition: 0.3s;
}

h2 {
  font-size: 1.6rem;
  margin-bottom: 24px;
  color: #9cdcff;
  font-weight: 600;
}

/* ------------------------- */
/* INPUT FIELDS */
/* ------------------------- */
input {
  width: 100%;
  padding: 14px 16px;
  margin-bottom: 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 15px;
  transition: 0.25s ease;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: #00c8ff;
  box-shadow: 0 0 10px rgba(0, 200, 255, 0.5);
}

/* ------------------------- */
/* PASSWORD FIELD */
/* ------------------------- */
.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 44px;
}

.eye-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 18px;
  color: #9cdcff;
  opacity: 0.75;
  transition: 0.3s;
}

.eye-icon:hover {
  color: #00c8ff;
  opacity: 1;
}

/* ------------------------- */
/* FORGOT PASSWORD */
/* ------------------------- */
.forgot-password {
  color: #9cdcff;
  font-size: 0.9rem;
  text-align: right;
  margin-top: -8px;
  margin-bottom: 16px;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.25s;
}

.forgot-password:hover {
  color: #00c8ff;
}

/* ------------------------- */
/* BUTTON */
/* ------------------------- */
button {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  background: linear-gradient(90deg, #0077ff, #00c8ff);
  box-shadow: 0 6px 20px rgba(0, 200, 255, 0.25);
  transition: all 0.25s ease;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 200, 255, 0.4);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ------------------------- */
/* FEEDBACK MESSAGES */
/* ------------------------- */
.error-msg {
  color: #ff7070;
  font-size: 0.9rem;
  margin-bottom: 6px;
}

.success-msg {
  color: #00e6a8;
  font-size: 0.9rem;
  margin-bottom: 6px;
}

/* ------------------------- */
/* SPINNER */
/* ------------------------- */
.spinner {
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid #00c8ff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-block;
  margin-right: 8px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ------------------------- */
/* RESPONSIVE */
/* ------------------------- */
@media (max-width: 480px) {
  .login-container {
    margin: 60px 16px;
    padding: 30px 24px;
  }

  h2 {
    font-size: 1.4rem;
  }
}
</style>